/**
 * Which listing request is allowed to write to the screen (task-djasurv8m7).
 *
 * ⛔⛔ SITE IDENTITY IS NOT REQUEST IDENTITY. The loader guarded its writes with
 * `prevSiteIdRef.current === requestSiteId` — which answers "is this still the same site?", NOT
 * "is this still the latest request?". Typing in the search box, changing a filter and paging all
 * launch overlapping requests FOR THE SAME SITE, and nothing orders their responses:
 *
 *   B (newer, filtered) fails  -> clears the rows, shows the failure
 *   A (older, unfiltered) then succeeds -> clears the error and installs ITS BROAD ROWS
 *                                          underneath B's filters
 *
 * ⇒ Which is the same widening this task is about, arriving by yet another door: rows on screen
 *   that are not an answer to the filters on screen. My failure-clearing made it MORE reachable,
 *   because now a superseded response can also wipe a valid one.
 * 🔑 A GUARD ON THE WRONG IDENTITY IS NOT A WEAKER GUARD, IT IS A DIFFERENT GUARD. The site check
 *   is correct about sites and silent about ordering, and it reads as if it covered both.
 *
 * ⛔ A COUNTER, NOT A KEY BUILT FROM THE FILTERS. A key derived from the current render's filters
 *   is rebuilt from the same closure that issued the request, so it always compares equal — an
 *   inert guard I shipped once already (#5010, the select-all race). A monotonic sequence cannot
 *   be reconstructed by the loser.
 */
export interface RequestGate {
  /** Claim the newest sequence number; every request calls this exactly once, before awaiting. */
  start(): number;
  /** True only for the most recently started request — the only one permitted to write. */
  isCurrent(seq: number): boolean;
}

export function createRequestGate(): RequestGate {
  let current = 0;
  return {
    start: () => (current += 1),
    isCurrent: (seq: number) => seq === current,
  };
}

/**
 * Claims a sequence for one request and returns the guard its writes must pass through.
 *
 * ⛔⛔ THE DECISION IS MADE WHEN THE WRITE HAPPENS, NEVER WHEN THE REQUEST STARTS (codex blocking on
 * both legs at 13da40e9d). The previous shape asked "am I still current?" and only THEN awaited
 * `response.json()` — so a request could pass the gate, park on the parse, and wake up to overwrite
 * a newer request that had started and finished in the meantime. Placing the check before an await
 * answers a question about a moment that has already passed.
 * 🔑 A GUARD EVALUATED BEFORE AN AWAIT IS A GUARD ABOUT THE PAST. Both predicates are therefore
 *   thunks called inside the returned function, at the instant of the write — `mayStillPaint` too,
 *   since the operator can change site during the same parse.
 *
 * ⚠️ `start()` is called HERE, synchronously, as the request is dispatched — ordering must be
 *   decided by dispatch order, not by who returns first.
 */
export function createCommitGuard(
  gate: RequestGate,
  mayStillPaint: () => boolean,
): (write: () => void) => void {
  const seq = gate.start();
  return (write: () => void) => {
    if (!mayStillPaint()) return;
    if (!gate.isCurrent(seq)) return;
    write();
  };
}
